import { z } from 'zod';
import {
  blockMetaSchema,
  createModel,
  currencySchema,
  languageSchema,
  partyRoleSchema,
  QueryMany,
  QueryOne,
  statusCommonSchema,
} from '../../common';
import { nameSchema, emailSchema, phoneSchema, dateSchema, objectIdSchema } from '../../validation';
import { userOrganizationSchema } from './user.org.model';
import { currentDeviceToken, userDeviceSchema } from './user.device.model';
import { UTagSharedSchema } from '../utag';
import { userMetaSchema } from './user.meta.model';

/**
 * `User` document — a natural person on the Hero network.
 *
 * Auth-v2 note (hard cutover): the legacy `{ groups, roles }` triplet
 * that used to live on `User.type` and `User.orgs[i].userType` has
 * been removed. Network identity is expressed via
 * {@link partyRoleSchema | `roles[]`}; per-org permission is
 * expressed via `orgs[i].memberType` (`admin` / `dispatcher` /
 * `driver` / `employee`). See
 * `apps/backend/src/app/auth/README.md`.
 */
export const UserSchema = createModel({
  status: statusCommonSchema,
  /** Set when `status === 'blocked'`; cleared on activate. Admin-only. */
  blockMeta: blockMetaSchema.optional(),
  preferredLanguage: languageSchema,
  /** the person name */
  name: nameSchema,
  /** the person phone number */
  phone: phoneSchema,
  currency: currencySchema,
  /** the organizations that the user belongs to */
  orgs: z.array(userOrganizationSchema),
  email: emailSchema.optional(),

  /**
   * the network roles this user plays. Multi-valued; ≥ 1. A single
   * user can be a `business_customer`, `carrier_company`, `broker`,
   * `individual_customer`, `driver`, or `contact` — and can hold more
   * than one at the same time. `roles[0]` is the convention for
   * primary-role display.
   *
   * Hero staff / super-admin users are NOT network parties — they are
   * tracked via the document's `creator` snapshot instead, and their
   * `AdminUser` record carries an empty `roles[]` (see
   * `admin-user.model.ts`).
   */
  roles: z.array(partyRoleSchema).min(1),

  /**
   * @deprecated Legacy "last push token written on sign-in". Not used
   * by the OneSignal send path (`include_aliases.external_id`).
   * Real auth sessions live on `RefreshSession`. See
   * [`docs/notifications.md`](../../../../../docs/notifications.md)
   * § "Deprecating `User.devices` / `oneSignalId`".
   */
  currentDeviceToken: currentDeviceToken.optional(),

  /**
   * @deprecated Legacy OneSignal subscription / player id snapshot.
   * Do **not** use as the push targeting key — sends use
   * `external_id = User._id`. OneSignal generates its own
   * `onesignal_id` per user profile inside each OneSignal app
   * (carrier vs customer are separate apps). See
   * [`docs/notifications.md`](../../../../../docs/notifications.md)
   * § "Deprecating `User.devices` / `oneSignalId`".
   */
  oneSignalId: currentDeviceToken.optional(),

  /**
   * @deprecated Legacy push-token registry (`UserDevice[]`). Historically
   * treated as "signed-in devices", but rows are notification tokens
   * (often empty for dashboard/web users). Multi-device push is owned by
   * OneSignal subscriptions under `external_id = User._id`; signed-in
   * sessions are `RefreshSession`; activity is `meta.lastSeenOnline`.
   * Planned for removal — see
   * [`docs/notifications.md`](../../../../../docs/notifications.md)
   * § "Deprecating `User.devices` / `oneSignalId`".
   */
  devices: z.array(userDeviceSchema),
  /** the organization that the user is currently signed at.
   *
   * Sometimes, a user can have `multiple` organization the he belongs to.
   *
   * In this case, the user `must` be logged in to one of them `one at a time`.
   * So this is the id of the current signed in org.
   *
   * If it's empty, then it means one of the following:
   * - the user does `NOT` belong to any organization such as a `freelancer_driver` or `individual_customer`.
   * - the user is created by a member but has NOT yet logged in to any of the organization.
   * - the user is logged out.
   */
  currentSignedInOrgId: objectIdSchema.optional(),
  lastSignIn: dateSchema.optional(),
  UTags: z.array(UTagSharedSchema),

  /**
   * Agent-facing signal layer — presence, last-known location
   * (accurate + approximate), channel policy + empirical
   * reachability, and mobile OS permission snapshots. See
   * [`docs/ai/user-signals.md`](../../../../../docs/ai/user-signals.md)
   * for the full read model and
   * [`docs/ai/proactive-outreach.md`](../../../../../docs/ai/proactive-outreach.md)
   * for the decision engine that consumes it.
   *
   * Optional at the schema level so pre-existing users read
   * cleanly. Gated at the DTO layer by `stripUserMeta` on
   * non-privileged read paths — see
   * `apps/backend/src/app/user/user.dto.service.ts`.
   */
  meta: userMetaSchema.optional(),
});
/** user db model */
export type User = z.infer<typeof UserSchema.model>;
export type UserData = z.infer<typeof UserSchema.data>;
export type UserPartial = z.infer<typeof UserSchema.partial>;
export type UserQueryMany = QueryMany<User>;
export type UserQueryOne = QueryOne<User>;

/** UserDeleted db model
 *
 * this is used when the user deletes his account ... we save it to this collection
 */
export const UserDeletedSchema = UserSchema;
export type UserDeleted = z.infer<typeof UserDeletedSchema.model>;
export type UserDeletedData = z.infer<typeof UserDeletedSchema.data>;
export type UserDeletedPartial = z.infer<typeof UserDeletedSchema.partial>;
